import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Square, Trash2 } from "lucide-react";
import axios from "axios";
import { supabase } from "../lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecordVideo = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoType, setVideoType] = useState("dance");

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/login");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setError("");
    } catch (err) {
      setError("No se pudo acceder a la cámara. Permite cámara y micrófono.");
      console.error(err);
    }
  };

  const startRecording = () => {
    if (!stream) return;

    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm;codecs=vp9",
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setIsPreviewing(true);

      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
      videoRef.current.src = URL.createObjectURL(blob);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setIsPreviewing(false);
    chunksRef.current = [];
    startCamera();
  };

  const handleUpload = async () => {
    if (!recordedBlob || !title) {
      setError("Completa el título.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError("Sesión inválida. Vuelve a entrar.");
        return;
      }

      const formData = new FormData();
      formData.append("file", recordedBlob, "recording.webm");
      formData.append("title", title);
      formData.append("description", description);
      formData.append("video_type", videoType);

      await axios.post(`${API}/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      setError(status ? `Error ${status}: ${detail}` : "Error subiendo el video");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="bg-white/10 border-b border-white/20">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="text-white flex items-center gap-2">
            <ArrowLeft size={18} /> Volver
          </Link>

          <div className="flex items-center gap-3">
            <span className="text-white text-2xl font-bold">
              Arti<span className="text-yellow-400">Book</span>
            </span>
            <button
              onClick={handleLogout}
              className="bg-white/20 text-white px-3 py-2 rounded-lg font-semibold hover:bg-white/30 transition text-xs md:text-base"
            >
              Salir
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl text-white text-center mb-6">Grabar vídeo</h1>

        {error && <div className="bg-red-600 text-white p-3 rounded mb-4">{error}</div>}

        <div className="bg-black rounded overflow-hidden mb-6">
          <video
            ref={videoRef}
            autoPlay={!isPreviewing}
            playsInline
            muted={!isPreviewing}
            controls={isPreviewing}
            className="w-full aspect-video"
          />
        </div>

        {!isPreviewing && (
          <div className="flex justify-center mb-6">
            {!isRecording ? (
              <button onClick={startRecording} className="bg-red-600 text-white px-6 py-3 rounded-full flex gap-2">
                <Camera /> Grabar
              </button>
            ) : (
              <button onClick={stopRecording} className="bg-yellow-400 text-purple-900 px-6 py-3 rounded-full flex gap-2">
                <Square /> Detener
              </button>
            )}
          </div>
        )}

        {isPreviewing && (
          <div className="bg-white/10 p-6 rounded">
            <input
              className="w-full mb-3 p-2 rounded"
              placeholder="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="w-full mb-3 p-2 rounded"
              placeholder="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <select className="w-full mb-4 p-2 rounded" value={videoType} onChange={(e) => setVideoType(e.target.value)}>
              <option value="dance">Danza</option>
              <option value="music">Música</option>
            </select>

            <div className="flex gap-3">
              <button onClick={handleUpload} disabled={uploading} className="flex-1 bg-green-600 text-white py-2 rounded">
                {uploading ? "Subiendo..." : "Guardar"}
              </button>
              <button onClick={discardRecording} className="bg-red-600 text-white px-4 rounded">
                <Trash2 />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordVideo;
